// Self-service "Response Guardrails" manager for Grace/Dawn (Voice Center).
// Lets authorized admins/QA reviewers create deterministic response rules —
// e.g. "Fundable360 enrollment fee is $2,995" — that outrank the LLM's own
// memory at answer time. See server/_core/responseGuardrails.ts for the
// runtime matcher every chat pipeline consults, and server/routers.ts
// (responseGuardrailsRouter) for the CRUD/test-playground procedures this
// component calls. Styling deliberately mirrors GraceFaqAdminTab.tsx (same
// card list + inline create/edit form idiom, no modal dialogs).
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Copy, ShieldCheck, ShieldOff, Eye, EyeOff,
  Archive, RotateCcw, FlaskConical, AlertTriangle, CheckCircle2, XCircle, History,
} from "lucide-react";

const VERTICALS = ["General", "Fundable360", "Scale360", "Legacy360", "Share360", "Media360"] as const;
const INTENT_CATEGORIES = ["pricing", "plan_recommendation", "processor_migration", "equipment_migration", "eligibility", "custom"] as const;
const INTENT_LABELS: Record<string, string> = {
  pricing: "Pricing", plan_recommendation: "Plan Recommendation", processor_migration: "Processor Migration",
  equipment_migration: "Equipment Migration", eligibility: "Eligibility", custom: "Custom",
};

type Vertical = (typeof VERTICALS)[number];
type IntentCategory = (typeof INTENT_CATEGORIES)[number];
type Audience = "grace" | "dawn" | "both";
type Status = "draft" | "published" | "archived";

type GuardrailForm = {
  name: string;
  vertical: Vertical;
  audience: Audience;
  intentCategory: IntentCategory;
  triggerPhrasesText: string;
  canonicalAnswer: string;
  mustIncludeText: string;
  mustNotSayText: string;
  priority: number;
  status: "draft" | "published";
  effectiveFrom: string;
  effectiveUntil: string;
  notes: string;
};

const emptyForm: GuardrailForm = {
  name: "", vertical: "General", audience: "both", intentCategory: "custom",
  triggerPhrasesText: "", canonicalAnswer: "", mustIncludeText: "", mustNotSayText: "",
  priority: 10, status: "draft", effectiveFrom: "", effectiveUntil: "", notes: "",
};

function linesToArray(text: string): string[] {
  return text.split("\n").map(s => s.trim()).filter(Boolean);
}

const STATUS_BADGE: Record<Status, string> = {
  draft: "bg-gray-100 text-gray-500 border-gray-200",
  published: "bg-emerald-50 text-emerald-600 border-emerald-200",
  archived: "bg-rose-50 text-rose-500 border-rose-200",
};

export function ResponseGuardrailsPanel() {
  const utils = trpc.useUtils();
  const listQuery = trpc.responseGuardrails.list.useQuery();
  const rules = (listQuery.data ?? []) as any[];

  const [form, setForm] = useState<GuardrailForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [filterVertical, setFilterVertical] = useState<"all" | Vertical>("all");
  const [filterStatus, setFilterStatus] = useState<"all" | Status>("all");
  const [showTester, setShowTester] = useState(false);
  const [historyId, setHistoryId] = useState<number | null>(null);

  const invalidate = () => utils.responseGuardrails.list.invalidate();

  const createMutation = trpc.responseGuardrails.create.useMutation({
    onSuccess: () => { invalidate(); setForm(null); toast.success("Guardrail saved"); },
    onError: (e) => toast.error(e.message),
  });
  const updateMutation = trpc.responseGuardrails.update.useMutation({
    onSuccess: () => { invalidate(); setEditId(null); setForm(null); toast.success("Guardrail updated"); },
    onError: (e) => toast.error(e.message),
  });
  const lifecycleMutation = trpc.responseGuardrails.setLifecycle.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const duplicateMutation = trpc.responseGuardrails.duplicate.useMutation({
    onSuccess: () => { invalidate(); toast.success("Duplicated as a new draft"); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMutation = trpc.responseGuardrails.delete.useMutation({
    onSuccess: () => { invalidate(); toast.success("Guardrail deleted"); },
    onError: (e) => toast.error(e.message),
  });

  const filtered = rules
    .filter(r => filterVertical === "all" || r.vertical === filterVertical)
    .filter(r => filterStatus === "all" || r.status === filterStatus);

  function startCreate() { setEditId(null); setForm({ ...emptyForm }); }
  function startEdit(r: any) {
    setEditId(r.id);
    setForm({
      name: r.name, vertical: r.vertical, audience: r.audience, intentCategory: r.intentCategory,
      triggerPhrasesText: (r.triggerPhrases ?? []).join("\n"),
      canonicalAnswer: r.canonicalAnswer,
      mustIncludeText: (r.mustIncludeFacts ?? []).join("\n"),
      mustNotSayText: (r.mustNotSayPhrases ?? []).join("\n"),
      priority: r.priority ?? 0,
      status: r.status === "archived" ? "draft" : r.status,
      effectiveFrom: r.effectiveFrom ? new Date(r.effectiveFrom).toISOString().slice(0, 10) : "",
      effectiveUntil: r.effectiveUntil ? new Date(r.effectiveUntil).toISOString().slice(0, 10) : "",
      notes: r.notes ?? "",
    });
  }
  function cancelForm() { setForm(null); setEditId(null); }

  function handleSave() {
    if (!form) return;
    const triggerPhrases = linesToArray(form.triggerPhrasesText);
    if (triggerPhrases.length === 0) { toast.error("Add at least one trigger phrase / example question."); return; }
    const payload = {
      name: form.name.trim(),
      vertical: form.vertical,
      audience: form.audience,
      intentCategory: form.intentCategory,
      triggerPhrases,
      canonicalAnswer: form.canonicalAnswer.trim(),
      mustIncludeFacts: linesToArray(form.mustIncludeText),
      mustNotSayPhrases: linesToArray(form.mustNotSayText),
      priority: form.priority,
      effectiveFrom: form.effectiveFrom ? new Date(form.effectiveFrom) : null,
      effectiveUntil: form.effectiveUntil ? new Date(form.effectiveUntil) : null,
      notes: form.notes.trim() || undefined,
    };
    if (editId !== null) {
      updateMutation.mutate({ id: editId, ...payload });
    } else {
      createMutation.mutate({ ...payload, status: form.status });
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Response Guardrails</h2>
          <p className="text-sm text-gray-400 mt-0.5 max-w-2xl">
            Deterministic, approved answers for Fundable360, Scale360, Legacy360, Share360, and Media360 questions. A <span className="font-semibold text-gray-500">published</span> guardrail wins over Grace/Dawn's own guess every time — no code deploy needed.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowTester(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-xl transition-colors ${showTester ? "bg-[#1A6FD4] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            <FlaskConical className="w-4 h-4" /> Test a Rule
          </button>
          <button
            onClick={startCreate}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#84ab4a] hover:bg-[#6e9038] text-white text-sm font-semibold rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" /> New Guardrail
          </button>
        </div>
      </div>

      {showTester && <GuardrailTester rules={rules} />}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {(["all", ...VERTICALS] as const).map(v => (
          <button key={v} onClick={() => setFilterVertical(v as any)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filterVertical === v ? "bg-[#84ab4a] text-white border-[#84ab4a]" : "border-gray-200 text-gray-500 hover:border-[#84ab4a] hover:text-[#84ab4a]"}`}>
            {v === "all" ? `All (${rules.length})` : v}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 self-center mr-1">Status</span>
        {(["all", "draft", "published", "archived"] as const).map(s => (
          <button key={s} onClick={() => setFilterStatus(s as any)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${filterStatus === s ? "bg-[#1A6FD4] text-white border-[#1A6FD4]" : "border-gray-200 text-gray-500 hover:border-[#1A6FD4] hover:text-[#1A6FD4]"}`}>
            {s}
          </button>
        ))}
      </div>

      {/* Create / Edit form */}
      {form && (
        <div className="bg-white border border-[#84ab4a]/30 rounded-2xl shadow-sm p-5 space-y-4">
          <div className="text-sm font-semibold text-gray-700">{editId !== null ? "Edit Guardrail" : "New Guardrail"}</div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Name</label>
            <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
              placeholder="e.g. Fundable360 enrollment fee"
              value={form.name} onChange={e => setForm(f => f ? { ...f, name: e.target.value } : f)} />
          </div>

          <div className="flex gap-3 flex-wrap">
            <div className="min-w-[160px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Vertical / Topic</label>
              <select className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.vertical} onChange={e => setForm(f => f ? { ...f, vertical: e.target.value as Vertical } : f)}>
                {VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Agent Audience</label>
              <select className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.audience} onChange={e => setForm(f => f ? { ...f, audience: e.target.value as Audience } : f)}>
                <option value="both">Both (Grace &amp; Dawn)</option>
                <option value="grace">Grace only</option>
                <option value="dawn">Dawn only</option>
              </select>
            </div>
            <div className="min-w-[180px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Intent / Category</label>
              <select className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.intentCategory} onChange={e => setForm(f => f ? { ...f, intentCategory: e.target.value as IntentCategory } : f)}>
                {INTENT_CATEGORIES.map(c => <option key={c} value={c}>{INTENT_LABELS[c]}</option>)}
              </select>
            </div>
            <div className="w-28">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Priority</label>
              <input type="number" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.priority} onChange={e => setForm(f => f ? { ...f, priority: parseInt(e.target.value) || 0 } : f)} />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Example Questions / Trigger Phrases (one per line)</label>
            <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a] resize-y font-mono"
              rows={4}
              placeholder={"What does Fundable360 cost?\nHow much is the enrollment fee?\nCost to get started with Fundable360"}
              value={form.triggerPhrasesText} onChange={e => setForm(f => f ? { ...f, triggerPhrasesText: e.target.value } : f)} />
            <p className="text-[11px] text-gray-400 mt-1">Include a few paraphrases, not just one exact wording — callers rarely ask the same way twice.</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Canonical Approved Answer</label>
            <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a] resize-y"
              rows={4}
              placeholder="Write the exact answer Grace/Dawn must give — plain language, no markdown, safe to speak aloud."
              value={form.canonicalAnswer} onChange={e => setForm(f => f ? { ...f, canonicalAnswer: e.target.value } : f)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Must Include (optional, one per line)</label>
              <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a] resize-y font-mono"
                rows={3} placeholder="$2,995"
                value={form.mustIncludeText} onChange={e => setForm(f => f ? { ...f, mustIncludeText: e.target.value } : f)} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Must NOT Say (optional, one per line)</label>
              <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a] resize-y font-mono"
                rows={3} placeholder="$29.95"
                value={form.mustNotSayText} onChange={e => setForm(f => f ? { ...f, mustNotSayText: e.target.value } : f)} />
            </div>
          </div>
          <p className="text-[11px] text-amber-600 -mt-2">These are enforced server-side: a rule can't be published while its own approved answer is missing a required fact or contains a forbidden phrase.</p>

          <div className="flex gap-3 flex-wrap">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Effective From (optional)</label>
              <input type="date" className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.effectiveFrom} onChange={e => setForm(f => f ? { ...f, effectiveFrom: e.target.value } : f)} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Effective Until (optional)</label>
              <input type="date" className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.effectiveUntil} onChange={e => setForm(f => f ? { ...f, effectiveUntil: e.target.value } : f)} />
            </div>
            {editId === null && (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Save as</label>
                <select className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                  value={form.status} onChange={e => setForm(f => f ? { ...f, status: e.target.value as any } : f)}>
                  <option value="draft">Draft (not live yet)</option>
                  <option value="published">Published (live immediately)</option>
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Admin Notes (optional)</label>
            <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a] resize-y"
              rows={2} placeholder="e.g. Source conflict between ghl.ts and the FAQ — confirm with client before publishing."
              value={form.notes} onChange={e => setForm(f => f ? { ...f, notes: e.target.value } : f)} />
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} disabled={isSaving || !form.name.trim() || !form.canonicalAnswer.trim()}
              className="px-4 py-2 bg-[#84ab4a] hover:bg-[#6e9038] text-white text-sm font-semibold rounded-xl disabled:opacity-40 transition-colors">
              {isSaving ? "Saving…" : editId !== null ? "Save Changes" : "Create Guardrail"}
            </button>
            <button onClick={cancelForm} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {/* List */}
      {listQuery.isLoading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-400">No guardrails yet. Create one to lock in an approved answer.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r: any) => (
            <div key={r.id} className={`bg-white border rounded-2xl px-5 py-4 ${r.status === "archived" || !r.isEnabled ? "opacity-50" : "border-gray-100"}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#84ab4a]/10 text-[#84ab4a] uppercase tracking-wide">{r.vertical}</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#1A6FD4]/10 text-[#1A6FD4] uppercase tracking-wide">
                      {r.audience === "both" ? "Grace & Dawn" : r.audience === "grace" ? "Grace only" : "Dawn only"}
                    </span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-500">{INTENT_LABELS[r.intentCategory] ?? r.intentCategory}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize ${STATUS_BADGE[r.status as Status]}`}>{r.status}</span>
                    {!r.isEnabled && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">DISABLED</span>}
                    <span className="text-[10px] text-gray-400">priority {r.priority}</span>
                  </div>
                  <div className="text-sm font-semibold text-gray-800 mb-1">{r.name}</div>
                  <div className="text-sm text-gray-500 whitespace-pre-wrap leading-relaxed">{r.canonicalAnswer}</div>
                  <div className="text-[11px] text-gray-400 mt-1.5">
                    Triggers: {(r.triggerPhrases ?? []).slice(0, 3).join(" · ")}{(r.triggerPhrases ?? []).length > 3 ? ` +${r.triggerPhrases.length - 3} more` : ""}
                  </div>
                  {r.notes && <div className="text-[11px] text-amber-600 mt-1 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{r.notes}</div>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                  {r.status !== "archived" && (
                    <button onClick={() => lifecycleMutation.mutate({ id: r.id, action: r.status === "published" ? "unpublished" : "published" })}
                      className={`p-1.5 rounded-lg transition-colors ${r.status === "published" ? "text-emerald-600 hover:bg-emerald-50" : "text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"}`}
                      title={r.status === "published" ? "Unpublish (revert to draft)" : "Publish (go live)"}>
                      {r.status === "published" ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  {r.status !== "archived" && (
                    <button onClick={() => lifecycleMutation.mutate({ id: r.id, action: r.isEnabled ? "disabled" : "enabled" })}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title={r.isEnabled ? "Disable" : "Enable"}>
                      {r.isEnabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  <button onClick={() => startEdit(r)} className="p-1.5 text-gray-400 hover:text-[#84ab4a] hover:bg-[#84ab4a]/8 rounded-lg transition-colors" title="Edit">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => duplicateMutation.mutate({ id: r.id })} className="p-1.5 text-gray-400 hover:text-[#1A6FD4] hover:bg-[#1A6FD4]/8 rounded-lg transition-colors" title="Duplicate">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setHistoryId(historyId === r.id ? null : r.id)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" title="Audit history">
                    <History className="w-3.5 h-3.5" />
                  </button>
                  {r.status === "archived" ? (
                    <button onClick={() => lifecycleMutation.mutate({ id: r.id, action: "restored" })} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Restore to draft">
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button onClick={() => { if (confirm(`Archive "${r.name}"? It will stop firing immediately.`)) lifecycleMutation.mutate({ id: r.id, action: "archived" }); }}
                      className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Archive">
                      <Archive className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => { if (confirm(`Permanently delete "${r.name}"? This cannot be undone.`)) deleteMutation.mutate({ id: r.id }); }}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {historyId === r.id && <GuardrailAuditHistory id={r.id} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GuardrailAuditHistory({ id }: { id: number }) {
  const detail = trpc.responseGuardrails.getById.useQuery({ id });
  const audit = (detail.data as any)?.audit ?? [];
  if (detail.isLoading) return <div className="text-[11px] text-gray-400 pt-2 border-t border-gray-50 mt-3">Loading history…</div>;
  if (audit.length === 0) return <div className="text-[11px] text-gray-400 pt-2 border-t border-gray-50 mt-3">No history yet.</div>;
  return (
    <div className="pt-2 mt-3 border-t border-gray-50 space-y-1">
      {audit.map((a: any) => (
        <div key={a.id} className="text-[11px] text-gray-500">
          <span className="font-semibold text-gray-700">{a.actor}</span> {a.action} this rule on {new Date(a.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          {a.summary ? ` — ${a.summary}` : ""}
        </div>
      ))}
    </div>
  );
}

// Client Test Playground — dry-runs the same matcher the live agents use.
function GuardrailTester({ rules }: { rules: any[] }) {
  const [question, setQuestion] = useState("");
  const [audience, setAudience] = useState<"grace" | "dawn">("grace");
  const [context, setContext] = useState("");
  const [onlyPublished, setOnlyPublished] = useState(true);

  const testMutation = trpc.responseGuardrails.testMatch.useMutation();

  function runTest(q?: string) {
    const useQuestion = q ?? question;
    if (!useQuestion.trim()) return;
    if (q) setQuestion(q);
    testMutation.mutate({
      question: useQuestion,
      audience,
      onlyPublished,
      recentUserTexts: context.trim() ? context.split("\n").map(s => s.trim()).filter(Boolean) : undefined,
    });
  }

  // Simple template-based paraphrase suggestions so a nontechnical admin can
  // validate a rule handles more than the exact wording they typed.
  const suggestions = question.trim()
    ? [
        `How much does ${question.replace(/^(what|how|does|is|cost of|price of)\b/gi, "").trim() || "it"} cost?`,
        `What's the cost to get started with ${question.match(/\b(fundable|scale|legacy|share|media)\s*360\b/i)?.[0] ?? "that"}?`,
        `Can you tell me more about ${question.replace(/[?.]/g, "").trim()}`,
      ].filter((s, i, arr) => arr.indexOf(s) === i)
    : [];

  const result = testMutation.data as any;

  return (
    <div className="bg-[#F8FAFC] border border-gray-200 rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <FlaskConical className="w-4 h-4 text-[#1A6FD4]" />
        <span className="text-sm font-bold text-gray-800">Test Rule Playground</span>
      </div>
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Caller Question</label>
          <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1A6FD4]"
            placeholder="e.g. What does Fundable360 cost to get started?"
            value={question} onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") runTest(); }} />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Agent</label>
          <select className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none" value={audience} onChange={e => setAudience(e.target.value as any)}>
            <option value="grace">Grace</option>
            <option value="dawn">Dawn</option>
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 pb-2.5">
          <input type="checkbox" checked={onlyPublished} onChange={e => setOnlyPublished(e.target.checked)} />
          Published rules only
        </label>
        <button onClick={() => runTest()} disabled={!question.trim() || testMutation.isPending}
          className="px-4 py-2 bg-[#1A6FD4] hover:bg-[#1558b0] disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition-colors">
          {testMutation.isPending ? "Testing…" : "Run Test"}
        </button>
      </div>
      <div>
        <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Prior conversation context (optional — one prior caller turn per line, oldest first)</label>
        <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#1A6FD4] resize-y"
          rows={2} placeholder={"Tell me about Scale360\nWhat plans do you have?"} value={context} onChange={e => setContext(e.target.value)} />
        <p className="text-[10px] text-gray-400 mt-1">Use this to test multi-turn scoping — e.g. a follow-up like "how much is that?" after a prior Scale360 turn.</p>
      </div>

      {suggestions.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Try a paraphrase</div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map(s => (
              <button key={s} onClick={() => runTest(s)} className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:border-[#1A6FD4] hover:text-[#1A6FD4] transition-colors">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div className="border-t border-gray-200 pt-3">
          {result.outcome === "match" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold">
                <CheckCircle2 className="w-4 h-4" /> Matched: {result.rule.name} <span className="text-gray-400 font-normal">(confidence {result.score.toFixed(2)}, vertical {result.rule.vertical})</span>
              </div>
              <div className="text-xs text-gray-500">Matched on: "{result.matchedPhrase}"</div>
              <div className="bg-white border border-gray-200 rounded-xl p-3 text-sm text-gray-700">
                <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Voice answer</div>
                {result.voiceAnswer}
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-3 text-sm text-gray-700">
                <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Text answer</div>
                {result.textAnswer}
              </div>
              {!result.constraintCheck.ok && (
                <div className="flex items-start gap-1.5 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-xl p-2.5">
                  <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div>This rule fails its own must-include/must-not-say constraints and will not fire live: {result.constraintCheck.violations.join("; ")}</div>
                </div>
              )}
            </div>
          )}
          {result.outcome === "ambiguous" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-amber-700 text-sm font-semibold">
                <AlertTriangle className="w-4 h-4" /> Ambiguous — {result.candidates.length} rules tie for this question
              </div>
              <div className="text-xs text-gray-600">Grace/Dawn would ask a clarifying question instead of guessing: "{result.clarifyingQuestion}"</div>
              <div className="space-y-1">
                {result.candidates.map((c: any) => (
                  <div key={c.rule.id} className="text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5">
                    <span className="font-semibold text-gray-700">{c.rule.name}</span> ({c.rule.vertical}, score {c.score.toFixed(2)})
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-amber-600">Fix: narrow the trigger phrases, or raise one rule's priority above the others, before publishing.</p>
            </div>
          )}
          {result.outcome === "none" && (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <XCircle className="w-4 h-4" /> No guardrail matched — Grace/Dawn would fall through to FAQ retrieval / normal generation for this question.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
